/**
 * Invoicing History Page
 *
 * Shows history of document submissions to Hacienda.
 * Mobile-first design with card layout for small screens.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  FileText,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  ArrowLeft,
  Eye,
  Download,
} from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/hooks/useAuth'
import api from '@/lib/api'
import { cn, formatCurrency, formatDateTime } from '@/lib/utils'
import { t } from '@/i18n'
import { useUrlPage, useUrlState } from '@/hooks/useUrlState'

interface SubmissionRecord {
  id: number
  bill_id: number
  bill_type: string
  client_name: string | null
  total: number
  hacienda_status: string | null
  hacienda_key: string | null
  consecutive: string | null
  created_at: string
  submitted_at: string | null
  attempts: number
  last_error: string | null
  pdf_url: string | null
  xml_url: string | null
  alanube_document_id: string | null
}

interface SubmissionListResponse {
  items: SubmissionRecord[]
  total: number
  page: number
  size: number
}

const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    label: t('history.statusPending'),
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  },
  submitted: {
    icon: Clock,
    label: t('history.statusSubmitted'),
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  accepted: {
    icon: CheckCircle2,
    label: t('history.statusAccepted'),
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  rejected: {
    icon: XCircle,
    label: t('history.statusRejected'),
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  failed: {
    icon: AlertTriangle,
    label: t('history.statusFailed'),
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
}

const DOCUMENT_TYPES: Record<string, string> = {
  ticket: t('history.docTicket'),
  invoice: t('history.docInvoice'),
  credit_note: t('history.docCreditNote'),
  debit_note: t('history.docDebitNote'),
}

export function InvoicingHistoryPage() {
  const navigate = useNavigate()
  const { isAdmin } = useAuth()

  const [page, setPage] = useUrlPage()
  const [statusFilter, setStatusFilter] = useUrlState('estado', 'all')
  const [search, setSearch] = useUrlState('q')
  const pageSize = 20

  const { data, isLoading, refetch, isFetching } = useQuery<SubmissionListResponse>({
    queryKey: ['invoicing-history', page, statusFilter, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        size: pageSize.toString(),
      })
      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }
      if (search) {
        params.append('search', search)
      }
      const response = await api.get(`/invoicing/submissions?${params}`)
      return response.data
    },
    enabled: isAdmin,
  })

  const getStatusConfig = (status: string | null) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.pending
  }

  const handleDownloadPDF = (pdfUrl: string) => {
    window.open(pdfUrl, '_blank')
  }

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">{t('incidents.noPermission')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} aria-label={t('common.back')}>
          <ArrowLeft className="h-5 w-5" aria-hidden="true" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold tracking-tight">{t('history.title')}</h1>
          <p className="text-sm text-muted-foreground truncate">
            {t('history.documentsCount', { count: data?.total ?? 0 })}
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => refetch()}
          disabled={isFetching}
          aria-label={t('common.refresh')}
        >
          <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} aria-hidden="true" />
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <Input
          placeholder={t('history.searchPlaceholder')}
          aria-label={t('history.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('history.filterByStatus')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('history.allStatuses')}</SelectItem>
            <SelectItem value="accepted">{t('history.statusAccepted')}</SelectItem>
            <SelectItem value="rejected">{t('history.statusRejected')}</SelectItem>
            <SelectItem value="submitted">{t('history.statusSubmitted')}</SelectItem>
            <SelectItem value="pending">{t('history.statusPending')}</SelectItem>
            <SelectItem value="failed">{t('history.statusFailed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : !data?.items.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">{t('history.empty')}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Mobile Card List */}
          <div className="space-y-3">
            {data.items.map((item) => {
              const statusConfig = getStatusConfig(item.hacienda_status)
              const StatusIcon = statusConfig.icon

              return (
                <Card key={item.id} className="overflow-hidden">
                  <CardContent className="p-4">
                    {/* Top row: Type + Status */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {DOCUMENT_TYPES[item.bill_type] || item.bill_type}
                      </span>
                      <Badge
                        variant="secondary"
                        className={cn('gap-1 text-xs', statusConfig.color)}
                      >
                        <StatusIcon className="h-3 w-3" />
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {/* Client name */}
                    <p className="font-medium truncate mb-1">
                      {item.client_name || t('incidents.generalClient')}
                    </p>

                    {/* Details row */}
                    <div className="flex items-center justify-between text-sm text-muted-foreground mb-3">
                      <span>{formatDateTime(item.created_at)}</span>
                      <span className="font-medium text-foreground">
                        {formatCurrency(item.total)}
                      </span>
                    </div>

                    {/* Consecutive */}
                    {item.consecutive && (
                      <p className="text-xs font-mono text-muted-foreground truncate mb-3">
                        {item.consecutive}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(`/bills/${item.bill_id}`)}
                      >
                        <Eye className="h-4 w-4 mr-2" aria-hidden="true" />
                        {t('common.view')}
                      </Button>
                      {item.pdf_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => handleDownloadPDF(item.pdf_url!)}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Pagination */}
          {data.total > pageSize && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('incidents.range', { from: (page - 1) * pageSize + 1, to: Math.min(page * pageSize, data.total), total: data.total })}
              </p>
              <Pagination
                currentPage={page}
                totalPages={Math.ceil(data.total / pageSize)}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
